import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../base-query';
import { serializeQueryArgs } from '../serialize-query-args';

export const TAX_SETTINGS_TAGS = Object.freeze({
  SETTINGS: 'tax_settings',
});

/**
 * ShowTaxSettingsController returns an EffectiveTaxSettingsResource without
 * withoutWrapping(), so the body is Laravel's { data: {...} } envelope —
 * unwrapped here the same way TaxCategoriesApiSlice does.
 */
const unwrapTaxSettingsResponse = (response) => response?.data ?? response ?? null;

/**
 * One set of endpoints for both screens; `scope` decides whether
 * TaxSettingsManager blanks x-on-behalf-of-company. Tagged by scope so saving
 * a restaurant's overrides never invalidates the partner screen's cache, which
 * shows different values entirely.
 */
export const taxSettingsApiSlice = createApi({
  reducerPath: 'taxSettingsApiSlice',
  baseQuery,
  tagTypes: Object.values(TAX_SETTINGS_TAGS),
  endpoints: (builder) => ({
    getTaxSettings: builder.query({
      query: ({ apiManager, ...rest }) => apiManager.index(rest),
      transformResponse: unwrapTaxSettingsResponse,
      providesTags: (_result, _error, { scope }) => [{ type: TAX_SETTINGS_TAGS.SETTINGS, id: scope }],
    }),
    /**
     * Never cached: it is read immediately before a save to be echoed back, so
     * a remembered count is the one thing it must not serve.
     */
    getTaxSettingsImpact: builder.query({
      query: ({ apiManager, ...rest }) => apiManager.impact(rest),
      transformResponse: unwrapTaxSettingsResponse,
      keepUnusedDataFor: 0,
    }),
    updateTaxSettings: builder.mutation({
      query: ({ apiManager, ...rest }) => apiManager.update(rest),
      transformResponse: unwrapTaxSettingsResponse,
      invalidatesTags: (_result, error, { scope }) => {
        if (error) {
          return [];
        }

        return [{ type: TAX_SETTINGS_TAGS.SETTINGS, id: scope }];
      },
    }),
  }),
  serializeQueryArgs,
});

export const { useGetTaxSettingsQuery, useLazyGetTaxSettingsImpactQuery, useUpdateTaxSettingsMutation } =
  taxSettingsApiSlice;
